// Даты доставки на витрине — одно правило для корзины и оформления.
//
// Источник истины — сервер (POST /orders/available-dates): он считает даты с суммарным
// спросом корзины и только в пределах горизонта каталога из настроек. Витрина разрешает
// выбрать ТОЛЬКО эти даты. Раньше оформление само «запрещало 30 дней вперёд, которых нет
// в списке», и при пустом списке первым свободным днём оказывался 31-й (30.09 → «31
// октября», за горизонтом и без наличия) — 30.09.2026, прод Стемса.

import { slotCutoffMinutes } from '../deliverySlots';

// Самая ранняя дата, на которую ещё можно оформить заказ: сегодня, а после отсечки
// последнего слота (начало слота − порог приёма) — завтра. now — время в поясе магазина
// (nowInTZ). Одно правило для корзины и оформления.
export function minOrderDate(now, timeSlots, leadTimeMinutes) {
  if (!timeSlots || timeSlots.length === 0) return now;  // конфиг ещё не загружен
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const lastCutoff = Math.max(...timeSlots.map(s => slotCutoffMinutes(s, leadTimeMinutes)));
  if (nowMinutes > lastCutoff) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }
  return now;
}

// Приём заказов на сегодня уже закрыт (после отсечки последнего слота) — то же правило, что
// у корзины и оформления (minOrderDate). Конфиг слотов не загружен — false: не пугаем зря.
// Блок «Сейчас нет в продаже» после отсечки называет ленту «Можно заказать на завтра».
export function isAfterTodayCutoff(now, timeSlots, leadTimeMinutes) {
  if (!timeSlots || timeSlots.length === 0) return false;
  return isoDate(minOrderDate(now, timeSlots, leadTimeMinutes)) !== isoDate(now);
}

// ['2026-10-05','2026-10-06','2026-10-08'] → «05.10–06.10, 08.10»
export function formatDateRanges(isoDates) {
  if (!isoDates || isoDates.length === 0) return '—';
  const fmt = iso => {
    const d = new Date(iso + 'T00:00:00');
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const ranges = [];
  let start = isoDates[0];
  let prev = isoDates[0];
  for (let i = 1; i < isoDates.length; i++) {
    const prevDate = new Date(prev + 'T00:00:00');
    const curDate = new Date(isoDates[i] + 'T00:00:00');
    const diffDays = Math.round((curDate - prevDate) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      prev = isoDates[i];
    } else {
      ranges.push(start === prev ? fmt(start) : `${fmt(start)}–${fmt(prev)}`);
      start = isoDates[i];
      prev = isoDates[i];
    }
  }
  ranges.push(start === prev ? fmt(start) : `${fmt(start)}–${fmt(prev)}`);
  return ranges.join(', ');
}

// Можно ли выбрать дату. allowedDates — список сервера; null — проверить не удалось
// (сеть): тогда не ограничиваем, сервер всё равно проверит наличие при создании заказа.
// Пустой массив — «общей даты нет»: запрещено всё.
export function isDateAllowed(dateStr, allowedDates) {
  if (!Array.isArray(allowedDates)) return true;
  return allowedDates.includes(dateStr);
}

// Date → 'YYYY-MM-DD' по его локальным полям (дата уже в поясе магазина — nowInTZ)
export function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Какие даты сервера реально можно выбрать: сервер всегда включает «сегодня», а после
// отсечки последнего слота принять заказ на сегодня уже нельзя (minIso = завтра).
// null (проверить не удалось) остаётся null — без ограничений.
export function selectableDates(serverDates, minIso) {
  if (!Array.isArray(serverDates)) return null;
  return serverDates.filter(d => d >= minIso);
}

// Как описать даты одной позиции. rawDates — даты сервера (включая «сегодня»),
// minIso — минимальная дата заказа (после отсечки — завтра). Три исхода:
//   ok     — есть даты для заказа: «05.10–11.10»;
//   cutoff — позиция была только на сегодня, а приём заказов на сегодня закрыт;
//   none   — дат нет вовсе (нет наличия или не хватает количества).
export const CUTOFF_NOTE = 'заказы на сегодня уже не принимаются';

export function describeDates(rawDates, minIso) {
  const all = [...(rawDates || [])].sort();
  const pickable = minIso ? all.filter(d => d >= minIso) : all;
  if (pickable.length) return { datesStr: formatDateRanges(pickable), reason: 'ok' };
  if (all.length) return { datesStr: CUTOFF_NOTE, reason: 'cutoff' };
  return { datesStr: 'нет доступных дат', reason: 'none' };
}

// Строки «позиция — когда доступна» для объяснения, почему у корзины нет общей даты.
// perItem — ответ сервера `per_item`: [{product_id|bouquet_id, dates}];
// names — { product: {id: name}, bouquet: {id: name} }; minIso — см. describeDates.
export function describeItemDates(perItem, names, minIso) {
  return (perItem || []).map(x => {
    const name = x.product_id != null
      ? names?.product?.[x.product_id]
      : names?.bouquet?.[x.bouquet_id];
    return { name: name || 'Позиция', ...describeDates(x.dates, minIso) };
  });
}

// Подсказка «уменьшите X до N шт — заказ соберётся на такие даты». Для каждого стебля
// ищем наибольшее количество (кратное шагу), при котором у него есть дата, общая со
// всеми остальными позициями. Помогает, когда выбрано больше, чем на складе, и стебель
// «уехал» на плановую поставку, разойдясь с остальными.
// positions: [{ name, requested, step, dates: string[] (при выбранном количестве, уже
// с учётом отсечки), capacity: {date: штук} | null }] — capacity есть только у стеблей.
export function suggestReductions(positions) {
  const out = [];
  positions.forEach((p, i) => {
    if (!p.capacity || !p.requested) return;
    const others = positions.filter((_, j) => j !== i);
    if (others.length === 0) return;
    let common = null;
    for (const o of others) {
      const s = new Set(o.dates || []);
      common = common === null ? [...s] : common.filter(d => s.has(d));
    }
    common = (common || []).sort();
    if (common.length === 0) return;
    const step = p.step || 1;
    let qty = 0;
    for (const d of common) qty = Math.max(qty, Math.floor((p.capacity[d] || 0) / step) * step);
    if (qty < step || qty >= p.requested) return;  // уменьшать некуда или не нужно
    out.push({ name: p.name, qty, dates: common.filter(d => (p.capacity[d] || 0) >= qty) });
  });
  return out;
}
